import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import mynaLogo from "@/assets/myna-logo.svg";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  const isActive = (path: string) =>
    pathname === path || (path !== "/" && pathname.startsWith(path + "/"));

  const linkClass = (path: string) =>
    isActive(path)
      ? "text-[var(--m-lime-text)] font-semibold transition-colors"
      : "text-[var(--m-muted)] hover:text-[var(--m-lime-text)] transition-colors";

  return (
    <nav
      aria-label="Main navigation"
      className="fixed top-0 left-0 right-0 z-50 bg-[var(--m-nav-bg)] backdrop-blur-xl border-b border-[var(--m-border-strong)]"
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-lime-500 focus:text-[#292929] focus:font-semibold focus:rounded-md"
      >
        Skip to main content
      </a>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">
          {/* Logo */}
          <div className="flex items-center gap-20">
            <Link to="/">
              <img src={mynaLogo} alt="MYNA AI" className="h-14 md:h-20 lg:h-[106px] w-auto opacity-90" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex gap-10 text-sm font-medium">
              <Link to="/" className={linkClass("/")}>
                Home
              </Link>
              <Link to="/how-it-works" className={linkClass("/how-it-works")}>
                How It Works
              </Link>
              <Link to="/solutions" className={linkClass("/solutions")}>
                Solutions
              </Link>
              {/* Mortgage Brokers always has lime accent — featured product */}
              <Link
                to="/solutions/mortgage-brokers"
                className="text-lime-400/90 hover:text-lime-300 font-semibold transition-colors"
              >
                Mortgage Brokers
              </Link>
              <Link to="/pricing" className={linkClass("/pricing")}>
                Pricing
              </Link>
            </div>
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/login">
              <button className="px-5 py-2.5 text-sm text-[var(--m-body)] hover:text-[var(--m-text-primary)] transition-colors font-medium">
                Sign In
              </button>
            </Link>
            <Link to="/request-demo">
              <Button className="px-6 py-2.5 bg-lime-500/80 text-[#292929] font-semibold text-sm hover:bg-lime-500 transition-all">
                Book Demo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2.5 text-[var(--m-muted)] hover:text-[var(--m-text-primary)]"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[var(--m-bg)] border-b border-[var(--m-border-strong)]"
            role="navigation"
            aria-label="Mobile navigation"
            onKeyDown={(e) => {
              if (e.key === "Escape") setIsOpen(false);
            }}
          >
            <div className="px-4 py-6 space-y-2">
              <Link
                to="/"
                className={`block py-3 text-base font-medium ${isActive("/") ? "text-[var(--m-lime-text)] font-semibold" : "text-[var(--m-heading)]"}`}
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/how-it-works"
                className={`block py-3 text-base font-medium ${isActive("/how-it-works") ? "text-[var(--m-lime-text)] font-semibold" : "text-[var(--m-heading)]"}`}
                onClick={() => setIsOpen(false)}
              >
                How It Works
              </Link>
              <Link
                to="/solutions"
                className={`block py-3 text-base font-medium ${isActive("/solutions") ? "text-[var(--m-lime-text)] font-semibold" : "text-[var(--m-heading)]"}`}
                onClick={() => setIsOpen(false)}
              >
                Solutions
              </Link>
              <Link
                to="/solutions/mortgage-brokers"
                className="block py-3 text-base text-lime-400/90 font-semibold"
                onClick={() => setIsOpen(false)}
              >
                Mortgage Brokers
              </Link>
              <Link
                to="/pricing"
                className={`block py-3 text-base font-medium ${isActive("/pricing") ? "text-[var(--m-lime-text)] font-semibold" : "text-[var(--m-heading)]"}`}
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
              <div className="pt-4 space-y-3">
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <button className="w-full px-5 py-3 text-base text-[var(--m-body)] hover:text-[var(--m-text-primary)] border border-[var(--m-border-strong)] transition-colors font-medium">
                    Sign In
                  </button>
                </Link>
                <Link to="/request-demo" onClick={() => setIsOpen(false)}>
                  <Button className="w-full px-6 py-3 bg-lime-500/80 text-[#292929] font-semibold text-sm hover:bg-lime-500 transition-all">
                    Book Demo
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
